import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { COMPANY } from "@/lib/company";
import { getActiveProvince } from "@/provinces/_registry";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema } from "@/lib/schema";

const UPDATED = "2026-05-01";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
  head: () => {
    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    const title = `นโยบายความเป็นส่วนตัว (PDPA) — Thai Notary ${config.nameEn}`;
    const description = `นโยบายคุ้มครองข้อมูลส่วนบุคคลตาม PDPA พ.ศ. 2562 ของ ${COMPANY.legalName} สาขา${config.nameTh} — สิทธิเจ้าของข้อมูล, ระยะเวลาเก็บ, ช่องทางใช้สิทธิ`;
    const head = pageHeadForProvince({ title, description, path: "/privacy" });
    return {
      ...head,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "นโยบายความเป็นส่วนตัว", url: `${site}/privacy` },
            ]),
          ),
        },
      ],
    };
  },
});

function PrivacyPage() {
  const { config } = getActiveProvince();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-14">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Privacy · PDPA Compliant</p>
        <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">นโยบายความเป็นส่วนตัว</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          อัปเดตล่าสุด: {new Date(UPDATED).toLocaleDateString("th-TH", { year: "numeric", month: "long", day: "numeric" })}
        </p>

        <div className="prose prose-sm mt-8 max-w-none space-y-6 text-foreground">
          <section>
            <h2 className="text-xl font-bold">1. ผู้ควบคุมข้อมูลส่วนบุคคล</h2>
            <p className="text-sm text-muted-foreground">
              {COMPANY.legalName} (ทะเบียนเลขที่ {COMPANY.registrationNumber}) ผู้ให้บริการในจังหวัด{config.nameTh}
              ปฏิบัติตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562 (PDPA) และแนวปฏิบัติของสภาทนายความฯ
              ติดต่อ DPO ได้ที่ {COMPANY.email} หรือโทร {COMPANY.phone}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">2. ข้อมูลที่เราเก็บรวบรวม</h2>
            <ul className="ml-5 list-disc text-sm text-muted-foreground">
              <li>ข้อมูลระบุตัวตน: ชื่อ–สกุล, เลขบัตรประชาชน/หนังสือเดินทาง, ที่อยู่, วันเดือนปีเกิด</li>
              <li>ข้อมูลติดต่อ: เบอร์โทร, อีเมล, LINE ID</li>
              <li>เอกสารที่ต้องการให้บริการ Notary / แปล / รับรอง</li>
              <li>ข้อมูลการชำระเงิน (ผ่านธนาคารคู่ค้า — เราไม่จัดเก็บเลขบัตรเครดิตเต็ม)</li>
              <li>ข้อมูลเทคนิคเมื่อใช้งานเว็บไซต์: IP, User-Agent, cookies เพื่อการวิเคราะห์</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold">3. วัตถุประสงค์ในการประมวลผล</h2>
            <ul className="ml-5 list-disc text-sm text-muted-foreground">
              <li>ให้บริการ Notary Public, แปลรับรอง, รับรองกงสุล ตามที่ลูกค้าร้องขอ</li>
              <li>ปฏิบัติตามกฎหมายภาษี, ป้องกันการฟอกเงิน (AML/KYC), และข้อบังคับสภาทนายความฯ</li>
              <li>การสื่อสารและการตลาด (เฉพาะกรณีได้รับความยินยอม)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold">4. ฐานทางกฎหมาย</h2>
            <p className="text-sm text-muted-foreground">
              เราประมวลผลข้อมูลตามฐานสัญญา (มาตรา 24(3)), ฐานหน้าที่ตามกฎหมาย (มาตรา 24(6)) และฐานความยินยอม (มาตรา 24(1))
              ในกรณีการตลาดและการใช้ cookies เพื่อการวิเคราะห์
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">5. ระยะเวลาเก็บรักษา</h2>
            <p className="text-sm text-muted-foreground">
              เอกสารทาง Notary และทะเบียนงานทนายความเก็บไว้ 10 ปี ตามข้อบังคับสภาทนายความฯ
              ข้อมูลบัญชี/ภาษีเก็บไว้ 5 ปีตามประมวลรัษฎากร ข้อมูลการตลาดเก็บไว้จนกว่าจะถอนความยินยอม
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">6. การเปิดเผยข้อมูลต่อบุคคลที่สาม</h2>
            <p className="text-sm text-muted-foreground">
              เปิดเผยเฉพาะที่จำเป็น เช่น กรมการกงสุล, สถานทูต/สถานกงสุล, กระทรวงยุติธรรม,
              สำนักงานตำรวจแห่งชาติ, และผู้ให้บริการ logistics (DHL/EMS) เพื่อจัดส่งเอกสาร
              ไม่ขายข้อมูลให้บุคคลภายนอกใดๆ
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">7. การส่งข้อมูลไปต่างประเทศ</h2>
            <p className="text-sm text-muted-foreground">
              ในกรณีบริการรับรองกงสุล/Apostille ข้อมูลอาจถูกส่งไปยังหน่วยงานในประเทศปลายทาง
              ภายใต้มาตรฐานคุ้มครองที่เหมาะสมตามมาตรา 28 PDPA
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">8. สิทธิของเจ้าของข้อมูล</h2>
            <ul className="ml-5 list-disc text-sm text-muted-foreground">
              <li>สิทธิเข้าถึงและขอสำเนา</li>
              <li>สิทธิแก้ไขให้ถูกต้อง</li>
              <li>สิทธิคัดค้านการประมวลผล</li>
              <li>สิทธิขอลบ/ทำลาย</li>
              <li>สิทธิขอให้ระงับการใช้</li>
              <li>สิทธิขอให้โอนย้ายข้อมูล</li>
              <li>สิทธิเพิกถอนความยินยอม</li>
              <li>สิทธิร้องเรียนต่อคณะกรรมการคุ้มครองข้อมูลส่วนบุคคล (สคส.)</li>
            </ul>
            <p className="mt-2 text-sm text-muted-foreground">
              ใช้สิทธิได้ที่ {COMPANY.email} หรือ LINE {COMPANY.lineId} ตอบกลับภายใน 30 วัน
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">9. มาตรการรักษาความปลอดภัย</h2>
            <p className="text-sm text-muted-foreground">
              เข้ารหัส TLS 1.3, จัดเก็บเอกสารในตู้นิรภัยที่สำนักงาน{config.nameTh},
              จำกัดสิทธิ์เข้าถึงเฉพาะทนายความและพนักงานที่เกี่ยวข้อง, มีนโยบายทำลายเอกสารตามรอบ
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">10. คุกกี้ (Cookies)</h2>
            <p className="text-sm text-muted-foreground">
              เว็บไซต์ใช้คุกกี้เพื่อการวิเคราะห์ (Google Analytics) และเพื่อจดจำการตั้งค่า ผู้ใช้สามารถปิดคุกกี้ผ่านการตั้งค่าเบราว์เซอร์ได้
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">11. หลักการประมวลผลข้อมูลตาม PDPA</h2>
            <p className="text-sm text-muted-foreground">
              {COMPANY.legalName} ยึดหลักการประมวลผลข้อมูลส่วนบุคคล 7 ข้อตามมาตรฐาน PDPA และ GDPR ได้แก่ (1) Lawfulness, Fairness และ Transparency — เก็บข้อมูลเฉพาะที่ลูกค้ารับรู้และยินยอม
              (2) Purpose Limitation — ใช้ข้อมูลเฉพาะวัตถุประสงค์ที่แจ้งไว้เท่านั้น เช่น เพื่อจัดทำ Notarization, แปลเอกสาร หรือยื่นต่อหน่วยงานราชการ
              (3) Data Minimization — เก็บเท่าที่จำเป็น ไม่ขอเอกสารส่วนตัวเกินขอบเขตงาน
              (4) Accuracy — มีกระบวนการตรวจสอบความถูกต้องของข้อมูลทุกครั้งก่อนนำไปประมวลผล
              (5) Storage Limitation — ลบหรือทำลายข้อมูลเมื่อพ้นระยะเวลาเก็บรักษา
              (6) Integrity และ Confidentiality — เข้ารหัสข้อมูลทั้งระหว่างส่งและขณะจัดเก็บ พร้อมระบบ access control
              (7) Accountability — มีผู้รับผิดชอบ DPO และทำบันทึกกิจกรรมประมวลผล (RoPA) ตามมาตรา 39
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">12. ข้อมูลส่วนบุคคลของผู้เยาว์และผู้ไร้ความสามารถ</h2>
            <p className="text-sm text-muted-foreground">
              สำหรับงานที่เกี่ยวข้องกับผู้เยาว์ (อายุต่ำกว่า 20 ปี) เช่น การรับรองสูติบัตร การยื่นวีซ่าครอบครัว
              หรือการจดทะเบียนรับบุตรบุญธรรม เราจะขอความยินยอมจากผู้ปกครองโดยชอบด้วยกฎหมายตามมาตรา 20 PDPA
              และจัดเก็บข้อมูลเฉพาะเท่าที่จำเป็นสำหรับกระบวนการนั้น ๆ พร้อมจำกัดสิทธิ์การเข้าถึงให้เฉพาะทนายผู้รับผิดชอบสำนวน
              กรณีบุคคลไร้ความสามารถหรือเสมือนไร้ความสามารถ จะดำเนินการผ่านผู้อนุบาล/ผู้พิทักษ์ที่ศาลแต่งตั้งเท่านั้น
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">13. การประมวลผลข้อมูลอ่อนไหว</h2>
            <p className="text-sm text-muted-foreground">
              งาน Notary บางประเภทอาจเกี่ยวข้องกับข้อมูลอ่อนไหวตามมาตรา 26 เช่น ใบรับรองแพทย์, ประวัติอาชญากรรม,
              ข้อมูลทางพันธุกรรม (กรณี DNA Paternity Test) — เราจะขอความยินยอมโดยชัดแจ้ง (Explicit Consent) แยกจากความยินยอมทั่วไป
              จัดเก็บแยกจากข้อมูลทั่วไป เข้ารหัสในระดับสูง และจำกัดผู้เข้าถึงเฉพาะทนายและพนักงานที่ลงนามใน NDA เป็นรายงานเฉพาะ
              ในกรณีบริการ Police Clearance หรือ Medical Council Certification ข้อมูลอ่อนไหวจะถูกลบทันทีหลังส่งมอบงาน
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">14. การโอนข้อมูลข้ามพรมแดน (Cross-border Transfer)</h2>
            <p className="text-sm text-muted-foreground">
              สำหรับงานรับรองกงสุล Apostille หรือยื่นวีซ่าต่างประเทศ ข้อมูลอาจถูกส่งไปยัง (ก) สถานทูต/สถานกงสุลในกรุงเทพ
              (ข) หน่วยงาน Apostille ในประเทศปลายทาง เช่น Secretary of State (USA), FCDO (UK), DFAT (Australia)
              (ค) ผู้ให้บริการแปลในต่างประเทศ (NAATI Australia, ATA USA, ITI UK)
              (ง) ผู้รับเอกสารตามที่ลูกค้าระบุ — ทุกการโอนผ่านมาตรฐาน Standard Contractual Clauses (SCCs) หรือ Adequacy Decision
              และจะดำเนินการเฉพาะเมื่อได้รับความยินยอมหรือเป็นไปตามฐานสัญญาเท่านั้น ตามมาตรา 28 และ 29 PDPA
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">15. การละเมิดข้อมูลส่วนบุคคล (Data Breach Notification)</h2>
            <p className="text-sm text-muted-foreground">
              หากเกิดเหตุการณ์ละเมิดข้อมูลส่วนบุคคล ทีม IT Security จะดำเนินการสอบสวนภายใน 24 ชั่วโมง,
              ประเมินความเสี่ยงต่อสิทธิและเสรีภาพของเจ้าของข้อมูล, และรายงานต่อสำนักงานคณะกรรมการคุ้มครองข้อมูลส่วนบุคคล (สคส.)
              ภายใน 72 ชั่วโมงตามมาตรา 37(4) PDPA หากเหตุการณ์อาจส่งผลกระทบสูงต่อเจ้าของข้อมูล จะแจ้งให้เจ้าของข้อมูลทราบโดยตรง
              ทาง Email และ LINE Official Account ภายในเวลาเดียวกัน พร้อมแนะนำมาตรการป้องกันความเสียหายเพิ่มเติม
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">16. การบันทึกกิจกรรมการประมวลผล (Records of Processing Activities — RoPA)</h2>
            <p className="text-sm text-muted-foreground">
              บริษัทจัดทำ RoPA ตามมาตรา 39 ครอบคลุม (ก) ประเภทข้อมูลที่ประมวลผล (ข) วัตถุประสงค์
              (ค) ฐานทางกฎหมาย (ง) ระยะเวลาเก็บรักษา (จ) ผู้รับข้อมูล (ฉ) มาตรการความปลอดภัย
              เอกสารนี้พร้อมให้เจ้าหน้าที่ สคส. ตรวจสอบได้ตลอดเวลา และทบทวนปีละครั้งหรือเมื่อมีการเปลี่ยนแปลงบริการสำคัญ
              ผู้รับผิดชอบ: DPO — {COMPANY.email}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">17. การเก็บข้อมูลด้วย Cookies และเทคโนโลยีติดตาม</h2>
            <p className="text-sm text-muted-foreground">
              เว็บไซต์ใช้คุกกี้ 4 ประเภทหลัก ได้แก่
              (1) Strictly Necessary Cookies — สำหรับการทำงานพื้นฐาน เช่น session, security
              (2) Performance Cookies — Google Analytics 4 (anonymized IP) เพื่อวิเคราะห์การใช้งาน
              (3) Functional Cookies — จดจำภาษาและการตั้งค่าผู้ใช้
              (4) Marketing Cookies — Meta Pixel, Google Ads (ต้องได้รับความยินยอมก่อน)
              ผู้ใช้สามารถจัดการความยินยอมผ่าน Cookie Consent Banner ที่ปรากฏในการเข้าชมครั้งแรก
              หรือเปลี่ยนแปลงได้ตลอดเวลาใน Browser Settings การปฏิเสธคุกกี้บางประเภทอาจทำให้บริการบางส่วนทำงานไม่สมบูรณ์
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">18. การปรับปรุงนโยบาย</h2>
            <p className="text-sm text-muted-foreground">
              นโยบายฉบับนี้อาจมีการปรับปรุงเป็นระยะตามการเปลี่ยนแปลงของกฎหมายหรือบริการ
              การเปลี่ยนแปลงสำคัญจะแจ้งผ่านหน้าเว็บไซต์และ Email ลูกค้าล่วงหน้าอย่างน้อย 30 วัน
              เวอร์ชันก่อนหน้าสามารถขอดูได้จาก DPO การใช้งานเว็บไซต์หรือบริการของเราต่อเนื่องหลังการประกาศ
              ถือว่ายอมรับนโยบายฉบับใหม่
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">19. ความร่วมมือกับหน่วยงานบังคับใช้กฎหมาย</h2>
            <p className="text-sm text-muted-foreground">
              บริษัทจะเปิดเผยข้อมูลตามคำสั่งศาลที่ออกอย่างถูกต้องตามกระบวนการ, หมายค้นจากพนักงานสอบสวน,
              หรือคำสั่งจากหน่วยงาน AML (ปปง.) เท่านั้น ก่อนเปิดเผยทุกครั้งจะตรวจสอบความถูกต้องของคำสั่ง
              และแจ้งเจ้าของข้อมูลให้ทราบ เว้นแต่กฎหมายห้ามแจ้ง (เช่นในคดีฟอกเงินหรือคดียาเสพติด)
              บริษัทไม่เคยและจะไม่เปิดเผยข้อมูลลูกค้าให้ฝ่ายใดโดยปราศจากคำสั่งทางกฎหมายที่ถูกต้อง
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">20. ช่องทางการร้องเรียน</h2>
            <p className="text-sm text-muted-foreground">
              เจ้าของข้อมูลที่เห็นว่าสิทธิตาม PDPA ถูกละเมิด สามารถร้องเรียนได้ 3 ช่องทาง
              (1) DPO ของบริษัทที่ {COMPANY.email} — ตอบกลับภายใน 7 วันทำการ
              (2) สำนักงานคณะกรรมการคุ้มครองข้อมูลส่วนบุคคล (สคส.) ที่ pdpc.or.th
              (3) ศาลแพ่ง/ศาลปกครองตามเขตอำนาจ — บริษัทยินดีให้ความร่วมมือเต็มที่ในทุกกระบวนการสอบสวน
              และให้ความช่วยเหลือทางกฎหมายแก่เจ้าของข้อมูลในการใช้สิทธิอย่างเต็มที่
            </p>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}